import React, { useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type {
  Equipamento,
  MovimentacaoItemExecucao,
  MovimentacaoItemIntake,
  TipoEquipamentoMovimentacao,
  MovimentacaoEquipamentoView,
  TipoFluxoMovimentacao,
} from '../../../models';
import { useVincularEquipamentoMutation, useMovimentacaoMutations, useEquipamentoQuery } from '../../../hooks/data';
import { normalizarPatrimonio } from '../../../models/equipamento.utils';
import { ehItemSimples } from '../../../models';
import BuscaAtivoInventario from '../../../components/BuscaAtivoInventario';

interface VincularEquipamentoSectionProps {
  solicitacaoId: string;
  movimentacaoId: string;
  equipamentosExecucao: MovimentacaoItemExecucao[];
  /** Pra o vínculo copiar a propriedade do ativo pro item (ver useVincularEquipamentoMutation). */
  equipamentosIntake: MovimentacaoItemIntake[];
  item: MovimentacaoEquipamentoView;
  /** Envio: a busca avisa quando o ativo não parece disponível (em uso, manutenção...). */
  tipoFluxo?: TipoFluxoMovimentacao;
}

/** Tipos da Movimentação são um subconjunto dos do Inventário — os demais (chip, cartão...)
 * não entram numa Movimentação de equipamento. */
const TIPOS_MOVIMENTACAO: TipoEquipamentoMovimentacao[] = [
  'imac',
  'macmini',
  'macbook',
  'notebook',
  'monitor',
  'smartphone',
  'cartao_acesso',
  'chip',
  'periferico',
  'outros',
];

/** Outros/Periférico não são ativos individualmente patrimoniados no GLPI (ex.: cabo,
 * mouse, adaptador) — não faz sentido exigir Patrimônio/Nº Série/busca no GLPI pra esses
 * tipos. Em vez disso, só uma descrição livre (decisão registrada). */
const TIPOS_SEM_PATRIMONIO: TipoEquipamentoMovimentacao[] = ['outros', 'periferico'];

/**
 * Vincula um item de equipamento (dentro de `equipamentosExecucao`) a um ativo do
 * Inventário. O inventário todo está no Digital (importado do GLPI), então só vincula ativo
 * que existe lá: "Buscar no Inventário" acha por patrimônio/nº série; não achou → "cadastrar
 * agora?" abre o Novo ativo já preenchido (ver BuscaAtivoInventario). Outros/Periférico não
 * são patrimoniados — continuam só com descrição.
 */
export default function VincularEquipamentoSection({
  solicitacaoId,
  movimentacaoId,
  equipamentosExecucao,
  equipamentosIntake,
  item,
  tipoFluxo,
}: VincularEquipamentoSectionProps) {
  const vincular = useVincularEquipamentoMutation(solicitacaoId);
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);
  // Processador/memória/disco vivem no Equipamento (si_equipamentos), não no item da
  // Movimentação — só existem depois de vinculado, por isso a query condicional.
  const { data: equipamentoVinculado } = useEquipamentoQuery(item.equipamentoId ?? undefined);

  const [patrimonioInput, setPatrimonioInput] = useState('');
  const [nSerieInput, setNSerieInput] = useState('');
  // Outros/Periférico: já vem com o que o solicitante descreveu ("Qual item?" na abertura).
  const [modeloInput, setModeloInput] = useState(item.descricaoLivre ?? '');
  // Ativo do Inventário achado pela busca — sem ele não vincula (exceto Outros/Periférico).
  const [ativo, setAtivo] = useState<Equipamento | null>(null);
  const [editando, setEditando] = useState(false);

  const semPatrimonio = item.tipoEquipamento ? TIPOS_SEM_PATRIMONIO.includes(item.tipoEquipamento) : false;

  const handleAbrirEdicao = () => {
    setPatrimonioInput(item.patrimonio ?? '');
    setNSerieInput(item.nSerie ?? '');
    setModeloInput(item.modelo ?? '');
    setAtivo(null);
    setEditando(true);
  };

  const handleRemoverVinculo = () => {
    const equipamentosExecucaoAtualizados = equipamentosExecucao.map((equipamento) =>
      equipamento.id === item.id
        ? { ...equipamento, equipamentoId: null, patrimonio: null, nSerie: null, modelo: null }
        : equipamento
    );
    atualizarMovimentacao.mutate({ id: movimentacaoId, equipamentosExecucao: equipamentosExecucaoAtualizados });
  };

  if (item.equipamentoId && !editando) {
    return (
      <div className="mt-4 border-t border-gray-100 pt-4 text-sm">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{semPatrimonio ? 'Descrição' : 'Equipamento vinculado'}</p>
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              onClick={handleAbrirEdicao}
              title="Trocar o equipamento vinculado"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-red-600"
              onClick={handleRemoverVinculo}
              title="Remover vínculo"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        {semPatrimonio ? (
          <p className="mt-2 text-gray-700">{item.modelo ?? '—'}</p>
        ) : (
          <dl className="mt-2 grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5">
            <dt className="text-gray-500">Patrimônio</dt>
            <dd className="m-0 text-gray-900"><span className="font-semibold">{normalizarPatrimonio(item.patrimonio) ?? '—'}</span></dd>
            <dt className="text-gray-500">Nº de série</dt>
            <dd className="m-0 text-gray-900">{item.nSerie ?? '—'}</dd>
            <dt className="text-gray-500">Modelo</dt>
            <dd className="m-0 text-gray-900">{item.modelo ?? '—'}</dd>
            {(equipamentoVinculado?.processador || equipamentoVinculado?.memoria || equipamentoVinculado?.tamanhoDisco) && (
              <>
                <dt className="text-gray-500">Configuração</dt>
                <dd className="m-0 text-gray-900">
                  {[equipamentoVinculado?.processador, equipamentoVinculado?.memoria, equipamentoVinculado?.tamanhoDisco]
                    .filter(Boolean)
                    .join(' · ')}
                </dd>
              </>
            )}
          </dl>
        )}
      </div>
    );
  }

  const handleVincular = () => {
    if (semPatrimonio) {
      if (!modeloInput.trim()) return;
      // Outros/Periférico não têm patrimônio de verdade — identificador interno só pra manter
      // o registro em si_equipamentos único por item (nunca mostrado ao SI).
      vincular.mutate(
        {
          movimentacaoId,
          equipamentosExecucao,
          itemId: item.id,
          patrimonio: `SEM-PATRIMONIO-${crypto.randomUUID().slice(0, 8)}`,
          nSerie: null,
          modelo: modeloInput.trim(),
          tipo: item.tipoEquipamento ?? 'outros',
        },
        { onSuccess: () => setEditando(false) }
      );
      return;
    }
    if (!ativo || (!ativo.modelo && !ehItemSimples(ativo.tipo))) return;
    const tipoDoAtivo = TIPOS_MOVIMENTACAO.includes(ativo.tipo as TipoEquipamentoMovimentacao)
      ? (ativo.tipo as TipoEquipamentoMovimentacao)
      : null;
    vincular.mutate(
      {
        movimentacaoId,
        equipamentosExecucao,
        itemId: item.id,
        equipamentosIntake,
        patrimonio: normalizarPatrimonio(ativo.patrimonio) ?? patrimonioInput.trim(),
        nSerie: ativo.nSerie ?? (nSerieInput.trim() || null),
        modelo: ativo.modelo ?? '',
        tipo: tipoDoAtivo ?? item.tipoEquipamento ?? 'outros',
      },
      { onSuccess: () => setEditando(false) }
    );
  };

  return (
    <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{semPatrimonio ? 'Descrição' : 'Vincular do Inventário'}</p>
      {!semPatrimonio && (
        <BuscaAtivoInventario
          empilhado
          valor={{ patrimonio: patrimonioInput, nSerie: nSerieInput, encontrado: ativo }}
          onChange={(valor) => {
            setPatrimonioInput(valor.patrimonio);
            setNSerieInput(valor.nSerie);
            setAtivo(valor.encontrado);
          }}
          paraEnvio={tipoFluxo === 'envio'}
          cadastroInicial={{ tipo: item.tipoEquipamento ?? '' }}
        />
      )}

      {/* Modelo vem do Inventário (o ativo é obrigatório) — só Outros/Periférico pedem descrição. */}
      {semPatrimonio && (
        <input
          type="text"
          className="input-gaveta w-full"
          placeholder="Descrição do(s) item(ns) *"
          value={modeloInput}
          onChange={(event) => setModeloInput(event.target.value)}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={handleVincular}
          // Sem modelo no ativo o termo não sai — completa a ficha no Inventário antes.
          disabled={
            vincular.isPending ||
            (semPatrimonio ? !modeloInput.trim() : !ativo || (!ativo.modelo && !ehItemSimples(ativo.tipo)))
          }
        >
          {semPatrimonio ? 'Salvar descrição' : 'Vincular equipamento'}
        </button>
        {editando && (
          <button type="button" className="text-sm text-gray-500 underline" onClick={() => setEditando(false)}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}
